import React, { useState, useEffect } from 'react';
import { Server as ServerType } from '../../types';
import { api } from '../../utils/api';
import { Activity, XCircle, BarChart2 as BandwidthIcon } from 'lucide-react';
import { 
    AreaChart, Area, XAxis, YAxis, CartesianGrid, 
    Tooltip, ResponsiveContainer 
} from 'recharts';

interface BandwidthDataPoint {
    time: any; // Usamos any temporalmente para manejar el timestamp crudo antes del format
    ingress: number;
    egress: number; 
}

interface ServerBandwidthModalProps {
    server: ServerType; 
    onClose: () => void;
    clientId?: string;
}

export const ServerBandwidthModal: React.FC<ServerBandwidthModalProps> = ({ server, onClose, clientId }) => {
    const [data, setData] = useState<BandwidthDataPoint[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [timeframe, setTimeframe] = useState(86400); 
    const [monthlyUsage, setMonthlyUsage] = useState<number | null>(null);
    const [monthlyLimit, setMonthlyLimit] = useState<number | null>(null);

    useEffect(() => {
        const fetchBandwidth = async () => {
            setLoading(true); // Activamos la animación de carga
            setError('');
            try {
                if (!clientId) throw new Error("Error: No se pudo verificar la identidad del cliente.");

                const response = await api.getClientBandwidth({
                    serverId: server.id,
                    royaleServiceId: server.royaleServiceId,
                    time: timeframe,
                    clientId: clientId
                });

                if (!response.success || !response.data) throw new Error(response.error || 'Error');

                const pointsData = response.data.points || [];
                
                let intervalInSeconds = 300; 
                if (pointsData.length > 1) {
                    intervalInSeconds = pointsData[1].time - pointsData[0].time;
                }
                if (intervalInSeconds <= 0) intervalInSeconds = 300;

                const formattedData = pointsData.map((point: any) => {
                    const ingressMbps = (point.netin * 8) / (intervalInSeconds * 1000000);
                    const egressMbps = (point.netout * 8) / (intervalInSeconds * 1000000);

                    return {
                        time: point.time, 
                        ingress: parseFloat(ingressMbps.toFixed(2)),
                        egress: parseFloat(egressMbps.toFixed(2)),
                    };
                });
                setData(formattedData);
                
                if (response.data.monthlyUsageTB !== undefined) {
                     setMonthlyUsage(parseFloat(response.data.monthlyUsageTB.toFixed(2)));
                }
                setMonthlyLimit(response.data.monthlyLimitTB || null);

            } catch (err: any) {
                setError(err.message);
            } finally {
                setLoading(false); // Desactivamos la animación cuando termina
            }
        };
        fetchBandwidth();
    }, [server.id, server.royaleServiceId, timeframe, clientId]);

    // --- FORMATOS DE EJES ---
    const formatXAxis = (timestamp: number) => {
        const date = new Date(timestamp * 1000);
        if (timeframe <= 86400) return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
        if (timeframe <= 604800) return date.toLocaleDateString('es-ES', { weekday: 'short', hour: '2-digit' });
        return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
    };

    const formatYAxis = (value: number) => {
        if (value >= 1000) return `${(value / 1000).toFixed(1)} Gbps`;
        return `${value} Mbps`;
    };

    return (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-[#141625] p-8 rounded-xl shadow-2xl w-full max-w-5xl border border-gray-800 relative min-h-[500px]">
                
                <button onClick={onClose} className="absolute top-6 right-6 text-gray-500 hover:text-white">
                    <XCircle className="w-6 h-6"/>
                </button>

                {/* Header */}
                <div className="mb-8">
                    <h3 className="text-2xl font-bold text-white">Ancho de Banda ({server.ip})</h3>
                    {(monthlyUsage !== null && monthlyLimit !== null) && (
                         <p className="text-sm text-gray-400 mt-1">
                            Usado: <span className="text-white font-bold">{monthlyUsage} TB</span> / Límite: <span className="text-white font-bold">{monthlyLimit} TB</span>
                            <span className="text-blue-400 text-xs ml-1">(Ampliable)</span>
                        </p>
                    )}
                </div>

                {/* Botones */}
                <div className="flex gap-2 mb-6 bg-[#1f2937] p-1 rounded-lg w-fit">
                    {[
                        { l: 'Hora', v: 3600 },
                        { l: 'Día', v: 86400 },
                        { l: 'Semana', v: 604800 },
                        { l: 'Mes', v: 2592000 }
                    ].map((opt) => (
                        <button 
                            key={opt.v} 
                            onClick={() => setTimeframe(opt.v)} 
                            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                                timeframe === opt.v 
                                    ? 'bg-blue-600 text-white shadow-lg' 
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                            }`}
                        >
                            {opt.l}
                        </button>
                    ))}
                </div>

                {/* ======================================================== */}
                {/* ESTADO DE CARGA (ANIMACIÓN)              */}
                {/* ======================================================== */}
                {loading && (
                    <div className="flex flex-col justify-center items-center h-[400px] w-full animate-in fade-in duration-300">
                        {/* Icono Activity con animate-pulse para el efecto latido */}
                        <Activity className="animate-pulse h-20 w-20 text-blue-500" />
                        <p className="mt-6 text-lg font-medium text-gray-300">Cargando ancho de banda...</p>
                    </div>
                )}

                {/* ======================================================== */}
                {/* ESTADO DE ERROR                      */}
                {/* ======================================================== */}
                {error && !loading && (
                    <div className="flex flex-col justify-center items-center h-[400px] w-full text-red-400">
                        <XCircle className="h-16 w-16 mb-4 opacity-80" />
                        <p className="text-lg">{error}</p>
                    </div>
                )}

                {/* ======================================================== */}
                {/* GRÁFICOS (DATA)                      */}
                {/* ======================================================== */}
                {!loading && !error && (
                    <div className="space-y-8 animate-in slide-in-from-bottom-4 duration-500">
                        {/* Gráfico INGRESO */}
                        <div>
                            <h4 className="text-sm font-bold text-gray-200 mb-2 pl-2 border-l-4 border-blue-500">Ingreso</h4>
                            <div className="h-[220px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={data}>
                                        <defs>
                                            <linearGradient id="colorIngress" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4}/>
                                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} opacity={0.2} />
                                        <XAxis 
                                            dataKey="time" 
                                            stroke="#6b7280" 
                                            fontSize={11} 
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={formatXAxis} 
                                            minTickGap={30}
                                        />
                                        <YAxis 
                                            stroke="#6b7280" 
                                            fontSize={11} 
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={formatYAxis}
                                            domain={[0, 'auto']}
                                            width={80}
                                        />
                                        <Tooltip 
                                            contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '8px' }}
                                            itemStyle={{ color: '#60a5fa' }}
                                            labelFormatter={(label) => formatXAxis(label)}
                                            formatter={(value: number) => [
                                                value >= 1000 ? `${(value/1000).toFixed(2)} Gbps` : `${value} Mbps`, 
                                                "Entrada"
                                            ]}
                                        />
                                        <Area type="monotone" dataKey="ingress" stroke="#3b82f6" strokeWidth={2} fill="url(#colorIngress)" activeDot={{ r: 4, strokeWidth: 0 }} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Gráfico SALIDA */}
                        <div>
                            <h4 className="text-sm font-bold text-gray-200 mb-2 pl-2 border-l-4 border-red-500">Salida</h4>
                            <div className="h-[220px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={data}>
                                        <defs>
                                            <linearGradient id="colorEgress" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.4}/>
                                                <stop offset="95%" stopColor="#f43f5e" stopOpacity={0}/>
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} opacity={0.2} />
                                        <XAxis 
                                            dataKey="time" 
                                            stroke="#6b7280" 
                                            fontSize={11} 
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={formatXAxis}
                                            minTickGap={30}
                                        />
                                        <YAxis 
                                            stroke="#6b7280" 
                                            fontSize={11} 
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={formatYAxis}
                                            domain={[0, 'auto']} 
                                            width={80}
                                        />
                                        <Tooltip 
                                            contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '8px' }}
                                            itemStyle={{ color: '#fb7185' }}
                                            labelFormatter={(label) => formatXAxis(label)}
                                            formatter={(value: number) => [
                                                value >= 1000 ? `${(value/1000).toFixed(2)} Gbps` : `${value} Mbps`, 
                                                "Salida"
                                            ]}
                                        />
                                        <Area type="monotone" dataKey="egress" stroke="#f43f5e" strokeWidth={2} fill="url(#colorEgress)" activeDot={{ r: 4, strokeWidth: 0 }} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};